// File: frontend/src/utils/canonicalUrl.ts

/**
 * Builds the canonical "{slug}--{id}" URL segments used by all five topic
 * domains. Mirror of the backend's SlugIdCodec — keep the two in sync.
 *
 * The id is what identifies the topic; the readable slug is decoration the
 * backend ignores whenever an id is present. That is what lets a title be
 * corrected, or a club renamed, without stranding links that were already
 * shared, indexed by Google, or written into the tail of an entry reference.
 *
 * Legacy URLs without an id still resolve: the backend falls back to a slug
 * lookup and answers 301 with the canonical address, which apiClient surfaces
 * as SlugRedirectError and pages navigate to. So call sites that have not been
 * migrated to these helpers keep working — they just cost one extra round trip
 * and land on the corrected URL.
 */

const SEPARATOR = "--";

/** Joins a readable slug and a numeric id into one URL segment. */
export const buildSlugSegment = (slug: string | null | undefined, id: number): string =>
  slug && slug.trim() !== "" ? `${slug}${SEPARATOR}${id}` : `${SEPARATOR}${id}`;

/** /gundem/{slug}--{id} */
export const customTopicUrl = (slug: string | null | undefined, id: number): string =>
  `/gundem/${buildSlugSegment(slug, id)}`;

/** /kisi/{slug}--{id} */
export const personUrl = (slug: string | null | undefined, id: number): string =>
  `/kisi/${buildSlugSegment(slug, id)}`;

/** /kulup/{slug}--{id} */
export const clubUrl = (slug: string | null | undefined, id: number): string =>
  `/kulup/${buildSlugSegment(slug, id)}`;

/**
 * /takim/{clubSlug}-{teamSlug}--{teamId}
 *
 * One segment, like the other four domains. The club slug is folded into the
 * readable part rather than being its own path segment — that keeps the URL
 * self-describing ("galatasaray-futbol--372" rather than "futbol--372") while
 * still resolving purely on the trailing id.
 */
export const teamUrl = (
  clubSlug: string,
  teamSlug: string | null | undefined,
  teamId: number
): string => {
  const readable = teamSlug && teamSlug.trim() !== "" ? `${clubSlug}-${teamSlug}` : clubSlug;
  return `/takim/${buildSlugSegment(readable, teamId)}`;
};

/** Absolute form of any of the above, for <link rel="canonical"> and og:url. */
export const absoluteUrl = (path: string): string =>
  `https://uckornerbirpenalti.com${path}`;
/**
 * /entry/{id} — the stable permalink of a single tanım.
 *
 * Nothing about the topic appears in it: not the slug, not the page number.
 * Both change over the life of an entry (a topic gets renamed, entries get
 * written before it, and the page it lands on depends on the READER's page
 * size preference), so neither can be frozen into a link that is meant to
 * still work months later. The id is the only stable part; everything else is
 * resolved at open time by EntryPermalinkPage.
 */
export const entryPermalinkPath = (entryId: number): string => `/entry/${entryId}`;

/**
 * Appends the CURRENT address's query string and fragment to a path.
 *
 * Used wherever the app navigates itself to a corrected address — a 301 from a
 * non-canonical slug, or a match page swapping a bare id for its readable
 * slug. Those navigations used to pass the path alone, which silently dropped
 * "?sayfa=4" and "#entry-1499": a shared deep link resolved its slug and then
 * landed on page 1 with no anchor, which looks exactly like pagination being
 * broken.
 */
export const withCurrentQueryAndHash = (path: string): string =>
  `${path}${window.location.search}${window.location.hash}`;